import * as React from 'react';

import { LoaderCircle } from 'lucide-react';

import { cn } from '../../lib/cn';

import {
  Button as PrimitiveButton,
  buttonVariants as primitiveButtonVariants,
} from '../../primitive/button';

export function buttonVariants({
  class: classValue,
  className,
  ...variants
}: NonNullable<Parameters<typeof primitiveButtonVariants>[0]> = {}) {
  return cn(primitiveButtonVariants(variants), 'text-sm font-normal', classValue, className);
}

export type ButtonProps = React.ComponentProps<typeof PrimitiveButton> & {
  loading?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { loading = false, asChild = false, children, className, disabled, onClickCapture, ...props },
    ref,
  ) => {
    return (
      <PrimitiveButton
        ref={ref}
        className={cn(
          'relative overflow-hidden text-sm font-normal not-disabled:cursor-pointer',
          loading && 'cursor-progress',
          className,
        )}
        disabled={disabled || loading}
        {...props}
        asChild={asChild}
        aria-busy={loading || props['aria-busy']}
        aria-disabled={(asChild && (disabled || loading)) || props['aria-disabled']}
        tabIndex={asChild && (disabled || loading) ? -1 : props.tabIndex}
        onClickCapture={(event) => {
          if (disabled || loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClickCapture?.(event);
        }}
      >
        {asChild ? (
          children
        ) : (
          <>
            <span
              aria-hidden={!loading}
              className={cn(
                'absolute inset-0 flex items-center justify-center transition-transform duration-200',
                loading ? 'translate-y-0' : 'translate-y-[150%]',
              )}
            >
              <LoaderCircle className="size-4 animate-spin" />
            </span>

            <span
              className={cn(
                'flex items-center justify-center gap-2 transition-transform duration-200',
                loading && '-translate-y-[150%]',
              )}
            >
              {children}
            </span>
          </>
        )}
      </PrimitiveButton>
    );
  },
);

Button.displayName = 'Button';
